/**
 * 本文将的唯一作用是根据传入的点集和SketchXXXObject对象,对SketchXXXObject进行分割, 分割成多段独立的SkecthXXXObject
 * 其中传入的点集方向上是有序的 [startPoint, 交点1, 交点2, endPoint], 如果只有一个交点则 [startPoint, 交点1, endPoint]
 */

import { SketchPointObject, SketchLineObject, SketchArcObject, SketchPointKind, SketchEllipseObject, SketchSplineObject, SketchParabolaObject, SketchCircleObject } from '../../mesh';
import { PointsMaterialFactory } from '../../material';
import { Vector3 } from 'three/webgpu';

class SplitEntityByIntersectPoints {
    /**
     * 根据有序点集分割线段。
     *
     * 说明：
     * 1. 点集约定：points = [startPoint, 交点1, 交点2, ..., endPoint]，
     *    首尾为原线两端点、中间为交点，相邻两点构成一段（如 2 个交点切出 3 段）；
     * 2. 每段使用相邻两个点对象构造新的 SketchLineObject，线对象是独立新实例，
     *    控制点沿用传入点集实例（调用方负责点集生命周期）；
     * 3. 辅助对象与无限长度标记随原线段透传，保证分割段语义与原线一致；
     * 4. 分割段仅用于预览渲染的几何读取（startPoint/endPoint 位置），不参与场景树挂载。
     *
     * @param points 有序控制点列（首尾为原线两端点，中间为交点）。
     * @param line 待分割的原始线段。
     * @returns 分割后的独立线段数组；点不足两个时返回空数组。
     */
    static splitLineByIntersectPoints(points: SketchPointObject[], line: SketchLineObject): SketchLineObject[] {
        // 点不足两个无法构成任何线段，直接返回空数组
        if (points.length < 2) {
            return [];
        }

        const referencePlane = line.referencePlane;
        const segments: SketchLineObject[] = [];

        // 相邻两点两两配对，每对构造一段独立线段
        for (let index = 0; index < points.length - 1; index += 1) {
            const segmentStart = points[index]!;
            const segmentEnd = points[index + 1]!;
            segments.push(new SketchLineObject({
                referencePlane,
                startPoint: segmentStart,
                endPoint: segmentEnd,
                isAuxiliaryObject: line.isAuxiliaryObject,
                isInfiniteLength: line.isInfiniteLength,
            }));
        }

        return segments;
    }

    /**
     * 根据有序点集分割圆弧。
     *
     * 说明：
     * 1. 点集约定与线段分割一致：points = [startPoint, 交点1, ..., endPoint]，
     *    首尾为原弧两端点、中间为交点（交点均位于原弧上），相邻两点构成一段子弧；
     * 2. 每段子弧复用原弧的圆心、半径、方向（clockwise）与采样段数，
     *    起止角由相邻两点相对圆心的极角确定，并按原方向归一化到同一 2π 窗口
     *    （顺时针终点角 ≤ 起点角，逆时针终点角 ≥ 起点角，与 SketchArcObject 内部逻辑一致）；
     * 3. 每段使用相邻两个点对象构造新的 SketchArcObject（控制点沿用传入实例），
     *    圆心控制点每段克隆独立实例，避免多段共享圆心点导致 connectedEdges 交叉；
     * 4. 分割段仅用于预览渲染的几何读取，不参与场景树挂载。
     *
     * @param points 有序控制点列（首尾为原弧两端点，中间为交点）。
     * @param arc 待分割的原始圆弧。
     * @returns 分割后的独立圆弧数组；点不足两个时返回空数组。
     */
    static splitArcByIntersectPoints(points: SketchPointObject[], arc: SketchArcObject): SketchArcObject[] {
        // 点不足两个无法构成任何弧段，直接返回空数组
        if (points.length < 2) {
            return [];
        }

        const referencePlane = arc.referencePlane;
        const definition = arc.getArcDefinition();
        const { centerWorld, radius, clockwise, segments } = definition;
        const centerLocal = referencePlane.worldToLocal(centerWorld.clone());
        const segmentsCount = segments ?? 64;
        const splitSegments: SketchArcObject[] = [];

        // 相邻两点两两配对，每对构造一段独立子弧
        for (let index = 0; index < points.length - 1; index += 1) {
            const segmentStart = points[index]!;
            const segmentEnd = points[index + 1]!;

            // 退化过滤：段两端点重合（零长度弧）时直接跳过
            if (segmentStart.position.distanceToSquared(segmentEnd.position) < 1e-12) {
                continue;
            }

            // 起止角 = 两点相对圆心的极角
            const startLocal = referencePlane.worldToLocal(segmentStart.position.clone());
            const endLocal = referencePlane.worldToLocal(segmentEnd.position.clone());
            const startAngle = Math.atan2(startLocal.y - centerLocal.y, startLocal.x - centerLocal.x);
            let endAngle = Math.atan2(endLocal.y - centerLocal.y, endLocal.x - centerLocal.x);

            // 按原弧方向把终点角归一化到与起点角同一 2π 窗口，保证扫掠方向与原弧一致
            if (clockwise) {
                if (endAngle > startAngle) {
                    endAngle -= Math.PI * 2;
                }
            } else if (endAngle < startAngle) {
                endAngle += Math.PI * 2;
            }

            // 每段独立克隆圆心控制点，避免多段共享圆心点导致 connectedEdges 交叉
            const segmentCenter = new SketchPointObject({
                position: centerWorld.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane,
            });

            splitSegments.push(new SketchArcObject({
                referencePlane,
                definition: {
                    centerWorld: centerWorld.clone(),
                    radius,
                    startAngle,
                    endAngle,
                    clockwise,
                    segments: segmentsCount,
                },
                startPoint: segmentStart,
                endPoint: segmentEnd,
                centerPoint: segmentCenter,
            }));
        }

        return splitSegments;
    }

    /**
     * 根据有序点集分割椭圆弧。
     *
     * 说明：
     * 1. 点集约定与线段分割一致：points = [startPoint, 交点1, ..., endPoint]，
     *    首尾为原椭圆弧两端点、中间为交点（交点均位于原椭圆弧上），相邻两点构成一段子弧；
     * 2. 每段子弧复用原椭圆的圆心、长短半轴、旋转角、方向（clockwise）与采样段数；
     * 3. 起止角由相邻两点反解椭圆参数角确定：点变换到椭圆主轴系后
     *    θ = atan2(y'/ry, x'/rx)（与 SketchEntityCut.cutEllipseByIntersection 同一口径），
     *    并按原方向归一化到同一 2π 窗口（顺时针终点角 ≤ 起点角，逆时针终点角 ≥ 起点角）；
     * 4. 每段使用相邻两个点对象构造新的 SketchEllipseObject（控制点沿用传入实例），
     *    中心点每段克隆独立实例，避免多段共享中心点导致 connectedEdges 交叉；
     * 5. 分割段仅用于预览渲染的几何读取，不参与场景树挂载。
     *
     * @param points 有序控制点列（首尾为原椭圆弧两端点，中间为交点）。
     * @param ellipse 待分割的原始椭圆弧。
     * @returns 分割后的独立椭圆弧数组；点不足两个时返回空数组。
     */
    static splitEllipseByIntersectPoints(points: SketchPointObject[], ellipse: SketchEllipseObject): SketchEllipseObject[] {
        // 点不足两个无法构成任何弧段，直接返回空数组
        if (points.length < 2) {
            return [];
        }

        const referencePlane = ellipse.referencePlane;
        const definition = ellipse.getEllipseDefinition();
        const { centerWorld, radiusX, radiusY, rotation, clockwise, segments } = definition;
        const centerLocal = referencePlane.worldToLocal(centerWorld.clone());
        const segmentsCount = segments ?? 64;
        const splitSegments: SketchEllipseObject[] = [];

        // 预计算旋转角正余弦，供主轴系坐标变换复用
        const cosRotation = Math.cos(rotation);
        const sinRotation = Math.sin(rotation);

        // 相邻两点两两配对，每对构造一段独立子弧
        for (let index = 0; index < points.length - 1; index += 1) {
            const segmentStart = points[index]!;
            const segmentEnd = points[index + 1]!;

            // 退化过滤：段两端点重合（零长度弧）时直接跳过
            if (segmentStart.position.distanceToSquared(segmentEnd.position) < 1e-12) {
                continue;
            }

            // 起止角 = 两点在椭圆主轴系下反解的椭圆参数角
            const resolveParameterAngle = (point: SketchPointObject): number => {
                const pointLocal = referencePlane.worldToLocal(point.position.clone());
                const qx = pointLocal.x - centerLocal.x;
                const qy = pointLocal.y - centerLocal.y;
                const xPrime = qx * cosRotation + qy * sinRotation;
                const yPrime = -qx * sinRotation + qy * cosRotation;
                return Math.atan2(yPrime / radiusY, xPrime / radiusX);
            };

            const startAngle = resolveParameterAngle(segmentStart);
            let endAngle = resolveParameterAngle(segmentEnd);

            // 按原方向把终点角归一化到与起点角同一 2π 窗口，保证扫掠方向与原椭圆弧一致
            if (clockwise) {
                if (endAngle > startAngle) {
                    endAngle -= Math.PI * 2;
                }
            } else if (endAngle < startAngle) {
                endAngle += Math.PI * 2;
            }

            // 每段独立克隆中心点，避免多段共享中心点导致 connectedEdges 交叉
            const segmentCenter = new SketchPointObject({
                position: centerWorld.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane,
            });

            splitSegments.push(new SketchEllipseObject({
                referencePlane,
                definition: {
                    centerWorld: centerWorld.clone(),
                    radiusX,
                    radiusY,
                    rotation,
                    startAngle,
                    endAngle,
                    clockwise,
                    segments: segmentsCount,
                },
                startPoint: segmentStart,
                endPoint: segmentEnd,
                centerPoint: segmentCenter,
            }));
        }

        return splitSegments;
    }
    
    /**
     * 根据有序点集分割样条曲线。
     *
     * 说明：
     * 1. 点集约定与线段分割一致：points = [startPoint, 交点1, ..., endPoint]，
     *    首尾为原样条两端点、中间为交点（交点均位于原曲线上），相邻两点构成一段子曲线；
     * 2. 以原样条采样点折线的累计长度为全局参数化，把每个端点/交点投影到折线得到
     *    全局参数 s（与 SketchEntityCut.cutSplineByIntersection 的最近投影同口径），
     *    段参数区间 [sA, sB] 内截取采样点子序列作为子曲线 curvePoints（首尾替换为精确位置）；
     * 3. 每段除端点外补一个中间控制点：在段参数区间中点 (sA+sB)/2 处沿折线插值得到世界点，
     *    新建 SketchPointObject 作为中间控制点（用户约定：除端点外还要有一个控制点，插值计算在曲线中间）；
     * 4. 曲线元数据继承原样条（含闭合标记）；分割段仅用于预览渲染的几何读取，不参与场景树挂载。
     *
     * @param points 有序控制点列（首尾为原样条两端点，中间为交点）。
     * @param spline 待分割的原始样条曲线。
     * @returns 分割后的独立样条曲线数组；点不足两个或原样条采样点不足时返回空数组。
     */
    static splitSplineByIntersectPoints(points: SketchPointObject[], spline: SketchSplineObject): SketchSplineObject[] {
        // 点不足两个无法构成任何子曲线，直接返回空数组
        if (points.length < 2) {
            return [];
        }

        const referencePlane = spline.referencePlane;
        const curvePoints = spline.getCurvePoints();
        if (curvePoints.length < 2) {
            return [];
        }

        // 1. 构建采样折线的累计长度表，作为全局参数化基础
        const cumulativeLengths: number[] = [0];
        for (let index = 1; index < curvePoints.length; index += 1) {
            cumulativeLengths.push(
                cumulativeLengths[index - 1]! + curvePoints[index]!.distanceTo(curvePoints[index - 1]!),
            );
        }
        const totalLength = cumulativeLengths[cumulativeLengths.length - 1]!;

        // 2. 把世界坐标点投影到采样折线，返回归一化全局参数 s（0~1）
        const resolveGlobalParameter = (position: Vector3): number => {
            if (totalLength <= 1e-12) {
                return 0;
            }
            let bestSegmentIndex = 0;
            let bestSegmentT = 0;
            let bestDistanceSq = Infinity;
            for (let index = 0; index < curvePoints.length - 1; index += 1) {
                const segmentDirection = curvePoints[index + 1]!.clone().sub(curvePoints[index]!);
                const segmentLengthSq = segmentDirection.lengthSq();
                const segmentT = segmentLengthSq > 1e-24
                    ? Math.max(0, Math.min(1,
                        position.clone().sub(curvePoints[index]!).dot(segmentDirection) / segmentLengthSq))
                    : 0;
                const projection = curvePoints[index]!.clone().addScaledVector(segmentDirection, segmentT);
                const distanceSq = position.distanceToSquared(projection);
                if (distanceSq < bestDistanceSq) {
                    bestDistanceSq = distanceSq;
                    bestSegmentIndex = index;
                    bestSegmentT = segmentT;
                }
            }
            return (cumulativeLengths[bestSegmentIndex]!
                + bestSegmentT * (cumulativeLengths[bestSegmentIndex + 1]! - cumulativeLengths[bestSegmentIndex]!))
                / totalLength;
        };

        // 3. 按归一化全局参数 s 在采样折线上线性插值取世界坐标点
        const resolvePointAtParameter = (parameter: number): Vector3 => {
            const targetLength = Math.max(0, Math.min(1, parameter)) * totalLength;
            let segmentIndex = 0;
            while (segmentIndex < curvePoints.length - 2 && cumulativeLengths[segmentIndex + 1]! < targetLength) {
                segmentIndex += 1;
            }
            const segmentLength = cumulativeLengths[segmentIndex + 1]! - cumulativeLengths[segmentIndex]!;
            const segmentT = segmentLength > 1e-12
                ? (targetLength - cumulativeLengths[segmentIndex]!) / segmentLength
                : 0;
            return curvePoints[segmentIndex]!.clone().lerp(curvePoints[segmentIndex + 1]!, segmentT);
        };

        // 4. 相邻两点两两配对，每对构造一段独立子曲线
        const splitSegments: SketchSplineObject[] = [];
        for (let index = 0; index < points.length - 1; index += 1) {
            const segmentStart = points[index]!;
            const segmentEnd = points[index + 1]!;

            // 退化过滤：段两端点重合（零长度曲线段）时直接跳过
            if (segmentStart.position.distanceToSquared(segmentEnd.position) < 1e-12) {
                continue;
            }

            const startParameter = resolveGlobalParameter(segmentStart.position);
            const endParameter = resolveGlobalParameter(segmentEnd.position);
            const lowParameter = Math.min(startParameter, endParameter);
            const highParameter = Math.max(startParameter, endParameter);

            // 退化过滤：段参数区间过小（端点/交点几乎重合于曲线同一位置）时直接跳过
            if (highParameter - lowParameter < 1e-9) {
                continue;
            }

            // 段中间插值控制点：在段参数区间中点沿折线插值得到世界点
            const midWorld = resolvePointAtParameter((lowParameter + highParameter) / 2);
            const midPoint = new SketchPointObject({
                position: midWorld,
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane,
            });

            // 段内采样点子序列：首尾替换为端点/交点精确位置，中间保留落在参数区间内的原采样点
            const segmentCurvePoints: Vector3[] = [segmentStart.position.clone()];
            const targetLowLength = lowParameter * totalLength;
            const targetHighLength = highParameter * totalLength;
            for (let pointIndex = 1; pointIndex < curvePoints.length - 1; pointIndex += 1) {
                const pointLength = cumulativeLengths[pointIndex]!;
                if (pointLength >= targetLowLength && pointLength <= targetHighLength) {
                    segmentCurvePoints.push(curvePoints[pointIndex]!.clone());
                }
            }
            segmentCurvePoints.push(segmentEnd.position.clone());

            splitSegments.push(new SketchSplineObject({
                referencePlane,
                startPoint: segmentStart,
                endPoint: segmentEnd,
                controlPoints: [midPoint],
                curvePoints: segmentCurvePoints,
                curveMetadata: spline.getCurveMetadata(),
            }));
        }

        return splitSegments;
    }


    /**
     * 根据有序点集分割抛物线。
     *
     * 说明：
     * 1. 点集约定与线段分割一致：points = [startPoint, 交点1, ..., endPoint]，
     *    首尾为原抛物线两端点、中间为交点（交点均位于原曲线上），相邻两点构成一段子曲线；
     * 2. 使用解析式参数分割而非折线投影：与 SketchParabolaObject 内部同一框架口径
     *    （axisDir = 焦点减顶点去法向分量后归一化，sideDir = axisDir × 参考面法向，
     *    参数 t = (点 - 顶点) · sideDir），相邻两点的侧向参数构成子曲线参数区间；
     * 3. 每段复用原抛物线的焦点/顶点与采样段数，起点/终点控制点沿用传入实例，
     *    焦点/顶点控制点每段克隆独立实例，避免多段共享控制点导致 connectedEdges 交叉；
     * 4. 分割段仅用于预览渲染的几何读取，不参与场景树挂载。
     *
     * @param points 有序控制点列（首尾为原抛物线两端点，中间为交点）。
     * @param parabola 待分割的原始抛物线。
     * @returns 分割后的独立抛物线数组；点不足两个时返回空数组。
     */
    static splitParabolaByIntersectPoints(points: SketchPointObject[], parabola: SketchParabolaObject): SketchParabolaObject[] {
        // 点不足两个无法构成任何子曲线，直接返回空数组
        if (points.length < 2) {
            return [];
        }

        const referencePlane = parabola.referencePlane;
        const definition = parabola.getParabolaDefinition();
        const { focalWorld, apexWorld, segments } = definition;
        const segmentsCount = segments ?? 64;

        // 与 SketchParabolaObject.resolveFrameFromDefinition 同一口径构建局部框架
        const normal = referencePlane.normal.clone().normalize();
        const axisVector = focalWorld.clone().sub(apexWorld);
        axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
        const axisDir = axisVector.normalize();
        const sideDir = new Vector3().crossVectors(axisDir, normal).normalize();

        // 把世界坐标点反解为抛物线侧向参数 t = (点 - 顶点) · sideDir
        const resolveParameter = (point: SketchPointObject): number => (
            point.position.clone().sub(apexWorld).dot(sideDir)
        );

        // 相邻两点两两配对，每对构造一段独立子曲线
        const splitSegments: SketchParabolaObject[] = [];
        for (let index = 0; index < points.length - 1; index += 1) {
            const segmentStart = points[index]!;
            const segmentEnd = points[index + 1]!;

            // 退化过滤：段两端点重合（零长度曲线段）时直接跳过
            if (segmentStart.position.distanceToSquared(segmentEnd.position) < 1e-12) {
                continue;
            }

            const startParameter = resolveParameter(segmentStart);
            const endParameter = resolveParameter(segmentEnd);

            // 退化过滤：段参数区间过小（端点/交点几乎重合于曲线同一位置）时直接跳过
            if (Math.abs(endParameter - startParameter) < 1e-9) {
                continue;
            }

            // 排序方向可能与参数方向相反（反向定义抛物线 startParameter > endParameter）：
            // 交换端点控制点使 startPoint 始终对应 startParameter（小参数端），
            // 避免 SketchParabolaObject 构造期 syncControlPointsFromDefinition
            // 把共享交点/端点控制点移动到错误的曲线位置，导致相邻段端点错位。
            const reversed = endParameter < startParameter;
            const definitionStart = reversed ? segmentEnd : segmentStart;
            const definitionEnd = reversed ? segmentStart : segmentEnd;

            // 每段独立克隆焦点/顶点控制点，避免多段共享控制点导致 connectedEdges 交叉
            const segmentFocalPoint = new SketchPointObject({
                position: focalWorld.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane,
            });
            const segmentApexPoint = new SketchPointObject({
                position: apexWorld.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane,
            });

            splitSegments.push(new SketchParabolaObject({
                referencePlane,
                definition: {
                    focalWorld: focalWorld.clone(),
                    apexWorld: apexWorld.clone(),
                    startParameter: Math.min(startParameter, endParameter),
                    endParameter: Math.max(startParameter, endParameter),
                    segments: segmentsCount,
                },
                focalPoint: segmentFocalPoint,
                apexPoint: segmentApexPoint,
                startPoint: definitionStart,
                endPoint: definitionEnd,
            }));
        }

        return splitSegments;
    }

    /**
     * 根据圆周上有序的交点分割圆为多段子弧。
     *
     * 说明：
     * 1. 点集约定：points = [交点1, 交点2, ..., 交点n]，交点已按圆周逆时针顺序排列；
     *    相邻两点构成一段子弧，最后一段回绕到首点，构成闭合环分割（n 个交点切出 n 段）；
     * 2. 每段子弧复用圆的圆心与半径，起止角 = 相邻两点相对圆心的极角，
     *    段方向取逆时针（终点角小于起点角时加 2π，与交点圆周顺序一致）；
     * 3. 每段使用相邻两个点对象构造新的 SketchArcObject（控制点沿用传入实例），
     *    圆心控制点每段克隆独立实例，避免多段共享圆心点导致 connectedEdges 交叉；
     * 4. 分割段仅用于预览渲染的几何读取，不参与场景树挂载；
     * 5. 单交点（相切）时首尾回绕成零长度段被过滤，返回空数组由调用方走整体判定兜底。
     *
     * @param points 圆周上有序的交点列（相邻构成一段子弧，最后一段回绕到首点）。
     * @param circle 待分割的原始圆。
     * @returns 分割后的独立子弧数组；交点不足两个时返回空数组。
     */
    static splitCircleByIntersectPoints(points: SketchPointObject[], circle: SketchCircleObject): SketchArcObject[] {
        // 交点不足两个无法构成任何子弧，直接返回空数组
        if (points.length < 2) {
            return [];
        }

        const referencePlane = circle.referencePlane;
        const centerWorld = circle.vec3dCenter;
        const radius = circle.radius;
        const centerLocal = referencePlane.worldToLocal(centerWorld.clone());
        const splitSegments: SketchArcObject[] = [];

        // 相邻交点两两配对，最后一段回绕到首点，构成闭合环分割
        for (let index = 0; index < points.length; index += 1) {
            const segmentStart = points[index]!;
            const segmentEnd = points[(index + 1) % points.length]!;

            // 退化过滤：相邻交点重合（零长度弧）时直接跳过
            if (segmentStart.position.distanceToSquared(segmentEnd.position) < 1e-12) {
                continue;
            }

            // 起止角 = 两点相对圆心的极角
            const startLocal = referencePlane.worldToLocal(segmentStart.position.clone());
            const endLocal = referencePlane.worldToLocal(segmentEnd.position.clone());
            const startAngle = Math.atan2(startLocal.y - centerLocal.y, startLocal.x - centerLocal.x);
            let endAngle = Math.atan2(endLocal.y - centerLocal.y, endLocal.x - centerLocal.x);

            // 交点按圆周逆时针顺序排列：终点角小于起点角（跨 0/±π 边界）时加 2π
            if (endAngle < startAngle) {
                endAngle += Math.PI * 2;
            }

            // 每段独立克隆圆心控制点，避免多段共享圆心点导致 connectedEdges 交叉
            const segmentCenter = new SketchPointObject({
                position: centerWorld.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane,
            });

            splitSegments.push(new SketchArcObject({
                referencePlane,
                definition: {
                    centerWorld: centerWorld.clone(),
                    radius,
                    startAngle,
                    endAngle,
                    clockwise: false,
                    segments: 64,
                },
                startPoint: segmentStart,
                endPoint: segmentEnd,
                centerPoint: segmentCenter,
            }));
        }

        return splitSegments;
    }
}

export { SplitEntityByIntersectPoints };
